/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { Button, FormGroup, Input, Label, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { TableInputProps } from '../InputInterface';
import { QUESTION } from '../../../../../../constants/terminology';

enum ACTIONS {
  REJECT = 'reject',
  NA = 'na',
  NR = 'nr',
};

const getLabel = (action?: ACTIONS) => {
  switch (action) {
    case ACTIONS.REJECT:
      return `Reject ${QUESTION.SINGULAR}`;
    case ACTIONS.NA:
      return 'Mark as N/A';
    case ACTIONS.NR:
      return 'Mark as N/R';
    default:
      return 'Please select an option';
  }
}

interface TableErrorModalProps extends Pick<TableInputProps, 'handleNA' | 'handleNR' | 'handleReject' | 'handleComments'> {
  setShowEmptyTableErrorModal: (isOpen: boolean) => void;
}

export const TableErrorModal = (props: TableErrorModalProps) => {
  const { setShowEmptyTableErrorModal, handleNA, handleNR, handleReject, handleComments } = props;
  const [action, setAction] = useState<ACTIONS | undefined>();

  if (!handleNA || !handleNR || !handleReject || !handleComments) {
    return null;
  }

  const handleCloseModal = () => setShowEmptyTableErrorModal(false);
  const handleSubmit = () => {
    switch (action) {
      case ACTIONS.REJECT:
        return handleReject();
      case ACTIONS.NA:
        return handleNA();
      case ACTIONS.NR:
        return handleNR();
      default:
        return;
    }
  }

  return (
    <Modal isOpen={true} toggle={handleCloseModal} backdrop='static'>
      <ModalHeader toggle={handleCloseModal}>Removing all data</ModalHeader>
      <ModalBody>
        <p>It appears you are trying to remove all data from this {QUESTION.SINGULAR}. Unfortunately, the {QUESTION.SINGULAR} has already been moved
          from unanswered to answered state and can’t be moved back (for provenance reasons). You can add a row of other data
          and then delete this row.</p>
        <p>Or, you can choose one of the following options:</p>
        <FormGroup>
          <FormGroup check>
            <Input type='radio' name='errorOptions' onClick={() => setAction(ACTIONS.REJECT)} />
            <Label check>
              Reject {QUESTION.SINGULAR} (recommended)
            </Label>
          </FormGroup>
          <FormGroup check>
            <Input type='radio' name='errorOptions' onClick={() => setAction(ACTIONS.NA)} />
            <Label check>
              Mark as N/A (not applicable to your company)
            </Label>
          </FormGroup>
          <FormGroup check>
            <Input type='radio' name='errorOptions' onClick={() => setAction(ACTIONS.NR)} />
            <Label check>
              Mark as N/R ({QUESTION.SINGULAR} is applicable but you choose not to report on it right now)
            </Label>
          </FormGroup>
        </FormGroup>
        <p> Note: None of these options will stop you adding in real data when you are ready.</p>
        <Input type='textarea' onChange={(e) => handleComments(e.target.value)} />
        <div className='mt-3 text-right'>
          <Button size='sm' color='link' onClick={handleCloseModal}>cancel</Button>
          <Button className='ml-2' onClick={handleSubmit} disabled={!action}>{getLabel(action)}</Button>
        </div>
      </ModalBody>
    </Modal>
  );
}
